import React, { useState, useEffect } from 'react';
import { Alert, Button, Space, Spin } from 'antd';
import { ReloadOutlined, ApiOutlined } from '@ant-design/icons';
import { unifiedApi } from '../services/unifiedApi';
import { config } from '../config';

interface ConnectionStatusProps {
  onConnectionChange?: (isConnected: boolean) => void;
  showRetry?: boolean;
}

/**
 * 백엔드 연결 상태를 체크하고 표시하는 컴포넌트
 */
const ConnectionStatus: React.FC<ConnectionStatusProps> = ({ 
  onConnectionChange, 
  showRetry = true 
}) => {
  const [isConnected, setIsConnected] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(true);

  const checkConnection = async () => {
    setIsChecking(true);
    setErrorMessage('');
    
    try {
      // 백엔드 헬스 체크
      const response = await fetch(config.healthCheckUrl);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ 백엔드 연결 성공:', data);
        setIsConnected(true);
        setShowSuccessMessage(true);
        onConnectionChange?.(true);

        // 10초 후 성공 메시지 자동 숨김
        setTimeout(() => {
          setShowSuccessMessage(false);
        }, 10000);
      } else {
        throw new Error(`HTTP ${response.status}: 서버 응답 오류`);
      }
    } catch (error) {
      console.error('❌ 백엔드 연결 실패:', error);
      setIsConnected(false);
      
      if (error instanceof TypeError && error.message.includes('fetch')) {
        setErrorMessage('백엔드 서버에 연결할 수 없습니다. 서버가 실행 중인지 확인하세요.');
      } else {
        setErrorMessage(`연결 오류: ${error instanceof Error ? error.message : '알 수 없는 오류'}`);
      }
      
      onConnectionChange?.(false);
    } finally {
      setIsChecking(false);
    }
  };

  useEffect(() => {
    checkConnection();
    
    // 2분마다 자동 재시도 (Rate Limiting 방지)
    const interval = setInterval(checkConnection, 120000);
    
    return () => clearInterval(interval);
  }, []);

  if (isChecking && isConnected === null) {
    return (
      <Alert
        message={
          <Space>
            <Spin size="small" />
            백엔드 서버 연결 상태 확인 중...
          </Space>
        }
        type="info"
        showIcon={false}
        style={{ marginBottom: 16 }}
      />
    );
  }

  if (isConnected === false) {
    return (
      <Alert
        message="백엔드 서버 연결 실패"
        description={
          <div>
            <p>{errorMessage}</p>
            {showRetry && (
              <Space style={{ marginTop: 8 }}>
                <Button 
                  type="primary" 
                  icon={<ReloadOutlined />} 
                  size="small"
                  loading={isChecking}
                  onClick={checkConnection}
                >
                  다시 시도
                </Button>
                <Button 
                  type="link" 
                  icon={<ApiOutlined />} 
                  size="small"
                  onClick={() => window.open(config.healthCheckUrl, '_blank')}
                >
                  서버 상태 확인
                </Button>
              </Space>
            )}
          </div>
        }
        type="error"
        showIcon
        closable={false}
        style={{ marginBottom: 16 }}
      />
    );
  }

  if (isConnected === true && showSuccessMessage) {
    return (
      <Alert
        message="백엔드 서버 연결 성공"
        description="서버와 정상적으로 연결되어 있습니다."
        type="success"
        showIcon
        closable
        onClose={() => setShowSuccessMessage(false)}
        style={{ marginBottom: 16 }}
      />
    );
  }

  return null;
};

export default ConnectionStatus;